/**
 * Safe diagnostics for subscribe / Paddle checkout (no secrets in console).
 * Prefix all logs with [ema-checkout] for filtering.
 */

const LOG_PREFIX = '[ema-checkout]'

/** First / last few chars only — never log full tokens. */
export function maskSecret(value: string | undefined, head = 8, tail = 4): string {
  if (!value || value.length === 0) return '(empty)'
  if (value.length <= head + tail) return `(len=${value.length}, redacted)`
  return `${value.slice(0, head)}…${value.slice(-tail)} (len=${value.length})`
}

export function classifyCheckoutRedirectUrl(url: string): {
  kind: 'paddle_hosted' | 'same_origin_account' | 'cross_origin_account' | 'unknown'
  hostname: string
  pathname: string
  note: string
} {
  try {
    const u = new URL(url)
    const host = u.hostname.toLowerCase()
    const isPaddle =
      host.includes('paddle.com') ||
      host.includes('paddlebilling.com') ||
      host.includes('buy.paddle') ||
      host.includes('checkout.paddle')
    if (isPaddle) {
      return {
        kind: 'paddle_hosted',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Browser should navigate to Paddle-hosted checkout',
      }
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const sameOrigin = origin && url.startsWith(origin)
    const isAccount = u.pathname.includes('/account')
    if (sameOrigin && isAccount) {
      return {
        kind: 'same_origin_account',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Direct redirect to this site /account — no Paddle page (plan change, downgrade, or misconfiguration)',
      }
    }
    if (isAccount) {
      return {
        kind: 'cross_origin_account',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Redirect to /account on another host — check WEB_APP_URL / PADDLE_CHECKOUT_RETURN_BASE',
      }
    }
    return {
      kind: 'unknown',
      hostname: u.hostname,
      pathname: u.pathname,
      note: 'Unexpected redirect target',
    }
  } catch {
    return {
      kind: 'unknown',
      hostname: '',
      pathname: '',
      note: `Invalid URL string (first 80 chars): ${String(url).slice(0, 80)}`,
    }
  }
}

/** Snapshot of Vite env baked in at build time (S3 deploy = set vars before npm run build, not Heroku). */
export function getBillingBuildSnapshot() {
  const raw = import.meta.env.VITE_PADDLE_CLIENT_TOKEN
  const hasToken = typeof raw === 'string' && raw.trim().length > 0
  return {
    mode: import.meta.env.MODE,
    dev: import.meta.env.DEV,
    prod: import.meta.env.PROD,
    VITE_SERVER_URL: import.meta.env.VITE_SERVER_URL || '(unset — using relative /api/v1)',
    VITE_FRONTEND_URL: import.meta.env.VITE_FRONTEND_URL || '(unset)',
    VITE_PADDLE_CLIENT_TOKEN_set: hasToken,
    VITE_PADDLE_CLIENT_TOKEN_preview: hasToken ? maskSecret(raw.trim()) : '(not set at build — overlay path disabled)',
    documentOrigin: typeof window !== 'undefined' ? window.location.origin : '(ssr)',
  }
}

export function logCheckout(tag: string, payload: Record<string, unknown>) {
  console.info(LOG_PREFIX, tag, payload)
}
